import React, { useEffect, useRef, useState } from 'react';
import { describeFiles, makeFilesetZip, verifyFilesReadback, verifyEdidReadback } from './fileset';

export function downloadBytes(name, bytes, type = 'application/octet-stream') {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = name; document.body.appendChild(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function CalibrationFiles({ files }) {
  const [manifest, setManifest] = useState([]), [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    let current = true; generation.current += 1;
    setManifest([]); setError('');
    describeFiles(files).then(value => { if (current) setManifest(value); }).catch(failure => { if (current) setError(failure.message); });
    return () => { current = false; generation.current += 1; };
  }, [files]);
  async function download() {
    const current = generation.current;
    setDownloading(true); setError('');
    try { const zip = await makeFilesetZip(files); if (current === generation.current) downloadBytes('northstar-fileset.zip', zip, 'application/zip'); }
    catch (failure) { if (current === generation.current) setError(failure.message); }
    finally { if (current === generation.current) setDownloading(false); }
  }
  return <div>
    <p>Extract the ZIP so the files remain inside the fileset folder on your transfer media. Follow the monitor's reviewed load instruction.</p>
    <button className="ni-button" type="button" disabled={downloading} onClick={download}>{downloading ? 'Preparing ZIP…' : 'Download calibration fileset ZIP'}</button>
    <div className="ni-table-wrap"><table className="ni-table"><thead><tr><th>File</th><th>SHA256</th></tr></thead>
      <tbody>{Object.entries(files).map(([name, bytes]) => <tr key={name}><td>{name} · {bytes.length} bytes</td>
        <td style={{ overflowWrap: 'anywhere', fontSize: '.8rem' }}>{manifest.find(item => item.name === name)?.sha256 || 'Calculating…'}</td></tr>)}</tbody></table></div>
    {error && <p role="alert">{error}</p>}
  </div>;
}

export default function CalibrationDelivery({ files, onContinue }) {
  const [copied, setCopied] = useState(null), [edid, setEdid] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false), [edidBusy, setEdidBusy] = useState(false);
  const [restart, setRestart] = useState(false), [independent, setIndependent] = useState(false);
  const [error, setError] = useState('');
  const copyGeneration = useRef(0), edidGeneration = useRef(0);
  useEffect(() => () => { copyGeneration.current += 1; edidGeneration.current += 1; }, []);
  async function selectCopied(event) {
    const selected = Array.from(event.target.files || []), current = ++copyGeneration.current;
    setCopied(null); setCopyBusy(true); setError(''); setRestart(false);
    try {
      if (!selected.length) return;
      await verifyFilesReadback(files, selected);
      const observed = Object.fromEntries(await Promise.all(selected.map(async file => [file.name, new Uint8Array(await file.arrayBuffer())])));
      const manifest = await describeFiles(observed);
      if (current === copyGeneration.current) setCopied(manifest);
    } catch (failure) { if (current === copyGeneration.current) setError(failure.message); }
    finally { if (current === copyGeneration.current) setCopyBusy(false); }
  }
  async function selectEdid(event) {
    const file = event.target.files?.[0], current = ++edidGeneration.current;
    setEdid(null); setEdidBusy(true); setError(''); setIndependent(false);
    try {
      if (!file) return;
      if (file.size > 16384) throw new Error('The independently captured EDID exceeds 16 KiB.');
      const verified = await verifyEdidReadback(files['edid-dp1.txt'], new Uint8Array(await file.arrayBuffer()));
      if (current === edidGeneration.current) setEdid(verified);
    } catch (failure) { if (current === edidGeneration.current) setError(failure.message); }
    finally { if (current === edidGeneration.current) setEdidBusy(false); }
  }
  return <section className="ni-panel" style={{ padding: '1rem' }} aria-label="Load and read back calibration">
    <h2>Load and read back the files</h2>
    <CalibrationFiles files={files} />
    <p>A download alone does not verify the destination or the monitor. Select the actual copied files for a byte comparison, then load the monitor and provide its independently captured EDID.</p>
    <label style={{ display: 'block', margin: '1rem 0' }}>Actual copied files
      <input type="file" multiple onChange={selectCopied} style={{ display: 'block', marginTop: '.4rem' }} />
    </label>
    {copyBusy && <p role="status">Checking selected copied-file bytes…</p>}
    {copied && <p role="status">Selected copied-file bytes match all generated files.</p>}
    <label style={{ display: 'block', margin: '1rem 0' }}>Independently captured EDID
      <input type="file" onChange={selectEdid} style={{ display: 'block', marginTop: '.4rem' }} />
    </label>
    {edidBusy && <p role="status">Comparing captured EDID bytes…</p>}
    {edid && <p role="status" style={{ overflowWrap: 'anywhere' }}>Selected EDID bytes match · SHA256 {edid.sha256}</p>}
    <label style={{ display: 'block', margin: '.8rem 0' }}><input type="checkbox" checked={restart} onChange={event => setRestart(event.target.checked)} /> I loaded this fileset on the subject monitor and restarted it.</label>
    <label style={{ display: 'block', margin: '.8rem 0' }}><input type="checkbox" checked={independent} onChange={event => setIndependent(event.target.checked)} /> I captured the selected EDID independently from this monitor after loading.</label>
    <p style={{ color: 'var(--ni-text-muted)' }}>The file comparisons check the selected bytes. Destination, physical loading and independent capture are operator attestations.</p>
    {error && <p role="alert">{error}</p>}
    <button className="ni-button ni-button--primary" type="button" disabled={!copied || !edid || !restart || !independent || copyBusy || edidBusy}
      onClick={() => onContinue({ filesetReadback: true, edidReadback: true, evidence: { copiedFiles: copied, edidSha256: edid.sha256,
        edidBytes: 128, restartConfirmed: true, independentCaptureConfirmed: true } })}>Continue to optical verification</button>
  </section>;
}
